import React, {useCallback, useEffect, useState} from "react";
import {Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        FormControlLabel, LinearProgress, Paper, Radio, Typography} from "@mui/material";
import {CheckCircleOutlined, ErrorOutlineOutlined, ScheduleOutlined} from "@mui/icons-material";

import {AttemptResult, QuizToTake, TrainingDue, completeTraining, getMyTraining, getQuizToTake,
        submitAttempt} from "../data/training";
import {useSessionContext} from "../util/SessionContext";
import PageHeader from "../components/Common/PageHeader";
import {Blue3, Grey2, Grey5, Grey6, Grey17} from "../theme/styles";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import PasswordField from "../components/Common/PasswordField";

// What the signed-in person owes, and the place they do it.
//
// ONE PAGE FOR THE WHOLE ACT: see what is outstanding, read the record, take the quiz, sign. Splitting
// taking from completing across screens would leave people passing a quiz and never attesting, which is
// the state that looks trained and is not.

const STATE_STYLE: Record<string, {colour: string; label: string; icon: React.ReactNode}> = {
    OVERDUE: {colour: "#B42318", label: "Overdue", icon: <ErrorOutlineOutlined sx={{fontSize: 18}}/>},
    OUTSTANDING: {colour: "#B54708", label: "Outstanding", icon: <ScheduleOutlined sx={{fontSize: 18}}/>},
    COMPLETE: {colour: "#067647", label: "Complete", icon: <CheckCircleOutlined sx={{fontSize: 18}}/>},
};

export default function MyTraining() {
    const sessionContext = useSessionContext();
    const [due, setDue] = useState<TrainingDue[]>([]);
    const [outstanding, setOutstanding] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [taking, setTaking] = useState<TrainingDue | null>(null);
    const [quiz, setQuiz] = useState<QuizToTake | null>(null);
    const [picked, setPicked] = useState<Record<string, string[]>>({});
    const [result, setResult] = useState<AttemptResult | null>(null);
    const [busy, setBusy] = useState(false);

    const [signing, setSigning] = useState<TrainingDue | null>(null);
    const [password, setPassword] = useState("");
    const [acknowledged, setAcknowledged] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getMyTraining(sessionContext)
            .then(result => { setDue(result.Training); setOutstanding(result.Outstanding); setError(null); })
            .catch(problem => setError(problem?.response?.data?.Cause ?? "Could not read your training."))
            .finally(() => setLoading(false));
    }, [sessionContext]);

    useEffect(load, [load]);

    const startQuiz = async (row: TrainingDue) => {
        setBusy(true);
        setError(null);
        try {
            const loaded = await getQuizToTake(sessionContext, row.ObjectRevisionUSID);
            setQuiz(loaded);
            setTaking(row);
            setPicked({});
            setResult(null);
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not open that quiz.");
        } finally {
            setBusy(false);
        }
    };

    const choose = (questionUSID: string, choiceUSID: string, multi: boolean) => {
        setPicked(previous => {
            const current = previous[questionUSID] ?? [];
            if (!multi) return {...previous, [questionUSID]: [choiceUSID]};
            return {...previous,
                    [questionUSID]: current.includes(choiceUSID)
                        ? current.filter(one => one !== choiceUSID)
                        : [...current, choiceUSID]};
        });
    };

    const submit = async () => {
        if (!taking) return;
        setBusy(true);
        setError(null);
        try {
            const answers = Object.entries(picked).flatMap(([QuestionUSID, choices]) =>
                choices.map(ChoiceUSID => ({QuestionUSID, ChoiceUSID})));
            setResult(await submitAttempt(sessionContext, taking.ObjectRevisionUSID, answers));
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not submit that attempt.");
        } finally {
            setBusy(false);
        }
    };

    const sign = async () => {
        if (!signing) return;
        setBusy(true);
        setError(null);
        try {
            await completeTraining(sessionContext, signing.ObjectRevisionUSID, password, acknowledged);
            setSigning(null);
            setPassword("");
            setAcknowledged(false);
            setTaking(null);
            setQuiz(null);
            setResult(null);
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not record that.");
        } finally {
            setBusy(false);
        }
    };

    const openSigning = (row: TrainingDue) => {
        setSigning(row);
        setPassword("");
        // PRE-TICKED ONLY WHEN IT IS NOT ASKED FOR. When the record asks somebody to confirm they have
        // read it, the box starts empty -- a pre-ticked attestation is not one.
        setAcknowledged(!row.RequiresAcknowledgment);
    };

    if (loading) return <div style={{padding: 40}}><CircularProgress size={22}/></div>;

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
            {/* THE PERSONA BELONGS ON A "MY" PAGE MOST OF ALL: what is waiting on you depends
                    entirely on which persona you are, so a header that does not say which one
                    leaves the whole page unexplained. Every list page carries it; these two
                    did not. */}
            <PageHeader title="My Training">
                <PersonaHeaderInfo/>
            </PageHeader>
            <div style={{padding: "20px 24px 48px", maxWidth: 1000}}>
                <Typography style={{fontSize: 13, color: Grey5, marginBottom: 20}}>
                    {outstanding === 0
                        ? "Nothing outstanding. Training you have completed is listed below."
                        : `${outstanding} ${outstanding === 1 ? "item" : "items"} outstanding.`}
                </Typography>

                {due.length === 0 ? (
                    <Typography style={{fontSize: 14, color: Grey6}}>
                        No curriculum in force names you. Training is assigned by a Training Curriculum.
                    </Typography>
                ) : due.map(row => {
                    const style = STATE_STYLE[row.State];
                    return (
                        <Paper key={row.ObjectRevisionUSID} elevation={1}
                               style={{padding: 16, marginBottom: 12, display: "flex", alignItems: "center",
                                       gap: 16, boxSizing: "border-box",
                                       // Colour carries the state before anybody reads a word of it.
                                       backgroundColor: row.State === "OVERDUE" ? "#FFFBFA"
                                           : row.State === "OUTSTANDING" ? "#FFFCF5" : undefined,
                                       border: `1px solid ${row.State === "OVERDUE" ? "#FDA29B"
                                           : row.State === "OUTSTANDING" ? "#FEDF89" : "transparent"}`}}>
                            <div style={{color: style.colour, display: "flex"}}>{style.icon}</div>
                            <div style={{flex: 1, minWidth: 0}}>
                                <Typography style={{fontSize: 15, color: Grey17}}>
                                    <span style={{color: Blue3, fontWeight: 600}}>{row.ObjectID}</span>
                                    {" "}{row.Name}
                                    {row.DisplayRevision ? (
                                        <span style={{color: Grey5}}> · Rev {row.DisplayRevision}</span>
                                    ) : null}
                                </Typography>
                                <Typography style={{fontSize: 12, color: Grey5, marginTop: 3}}>
                                    {style.label} — {row.Reason}
                                    {row.CurriculumName ? ` · from ${row.CurriculumName}` : ""}
                                </Typography>
                            </div>
                            {/* THE NUMBER, NOT JUST THE WORD. "Overdue" and "108 days overdue" ask for
                                different amounts of hurry, and the server did the arithmetic so the
                                two people looking at this screen see the same figure. */}
                            {row.DaysOverdue !== null && row.DaysOverdue >= 0 ? (
                                <Chip size="small" label={`${row.DaysOverdue}d overdue`}
                                      sx={{fontSize: 11, height: 22, fontWeight: 600,
                                           backgroundColor: "#FEE4E2", color: "#B42318"}}/>
                            ) : row.State !== "COMPLETE" && row.DueDatetime ? (
                                <Chip size="small" variant="outlined"
                                      label={`Due ${new Date(row.DueDatetime).toLocaleDateString()}`}
                                      sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            {row.RequiresQuiz ? <Chip size="small" label="Quiz" sx={{fontSize: 11, height: 22}}/> : null}
                            {row.Score !== null ? (
                                <Chip size="small" variant="outlined" label={`${row.Score}%`}
                                      sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            {row.State !== "COMPLETE" ? (
                                row.RequiresQuiz ? (
                                    <Button size="small" variant="contained" disabled={busy}
                                            onClick={() => startQuiz(row)} sx={{textTransform: "none"}}>
                                        Take the quiz
                                    </Button>
                                ) : (
                                    <Button size="small" variant="contained" disabled={busy}
                                            onClick={() => openSigning(row)} sx={{textTransform: "none"}}>
                                        Record training
                                    </Button>
                                )
                            ) : null}
                        </Paper>
                    );
                })}

                {error ? (
                    <Typography style={{fontSize: 13, color: "#B42318", marginTop: 12}}>{error}</Typography>
                ) : null}
            </div>

            {/* ── taking ─────────────────────────────────────────────────────────────────── */}
            <Dialog open={!!taking} onClose={() => { setTaking(null); setQuiz(null); setResult(null); }}
                    maxWidth="md" fullWidth>
                <DialogTitle style={{fontSize: 20, fontWeight: 700, color: Grey17}}>
                    {taking?.ObjectID} — {taking?.Name}
                </DialogTitle>
                <DialogContent>
                    {busy ? <LinearProgress sx={{marginBottom: 2}}/> : null}

                    {/* BEFORE SUBMITTING: no correct flags, no rationale -- the payload does not carry them. */}
                    {quiz && !result ? (
                        <>
                            <Typography style={{fontSize: 13, color: Grey5, marginBottom: 16}}>
                                Pass mark {quiz.PassingScore}%.
                                {quiz.Attempts > 0 ? ` You have taken this ${quiz.Attempts} time${quiz.Attempts === 1 ? "" : "s"}; best ${quiz.BestScore}%.` : ""}
                            </Typography>
                            {quiz.Questions.map((question, index) => {
                                const multi = question.QuestionType === "MULTI_SELECT";
                                const chosen = picked[question.USID] ?? [];
                                return (
                                    <div key={question.USID}
                                         style={{border: `1px solid ${Grey2}`, borderRadius: 10,
                                                 padding: 16, marginBottom: 12}}>
                                        <Typography style={{fontSize: 15, color: Grey17}}>
                                            {index + 1}. {question.Text}
                                        </Typography>
                                        {/* THE TAKER IS TOLD HOW MANY, which is what the author chose it for. */}
                                        <Typography style={{fontSize: 12, color: Grey6, marginTop: 2}}>
                                            Select {question.CorrectSelections} of these.
                                            {chosen.length !== question.CorrectSelections
                                                ? ` You have selected ${chosen.length}.` : ""}
                                        </Typography>
                                        {question.Choices.map(choice => (
                                            <div key={choice.USID}
                                                 style={{display: "flex", alignItems: "center", gap: 8,
                                                         marginTop: 6, cursor: "pointer"}}
                                                 onClick={() => choose(question.USID, choice.USID, multi)}>
                                                {multi
                                                    ? <Checkbox size="small" checked={chosen.includes(choice.USID)}/>
                                                    : <Radio size="small" checked={chosen.includes(choice.USID)}/>}
                                                <Typography style={{fontSize: 14, color: Grey17}}>{choice.Text}</Typography>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </>
                    ) : null}

                    {/* AFTER: the rationale for every choice, which is what it was written for. */}
                    {result ? (
                        <>
                            <Typography style={{fontSize: 17, fontWeight: 600,
                                                color: result.Passed ? "#067647" : "#B42318"}}>
                                {result.Score}% — {result.Passed ? "passed" : `not passed (${result.PassingScore}% needed)`}
                            </Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 4, marginBottom: 16}}>
                                {result.Passed
                                    ? "Sign below to record your training."
                                    : "Read the explanations, then take it again."}
                            </Typography>
                            {result.Questions.map((question, index) => (
                                <div key={question.QuestionUSID}
                                     style={{border: `1px solid ${Grey2}`, borderRadius: 10, padding: 16,
                                             marginBottom: 12}}>
                                    <Typography style={{fontSize: 15, color: Grey17}}>
                                        {index + 1}. {question.Text}
                                        <span style={{color: question.Correct ? "#067647" : "#B42318",
                                                      marginLeft: 8, fontSize: 13}}>
                                            {question.Correct ? "correct" : "not correct"}
                                        </span>
                                    </Typography>
                                    {question.Choices.map(choice => (
                                        <div key={choice.USID} style={{marginTop: 8}}>
                                            <Typography style={{fontSize: 14,
                                                                color: choice.IsCorrect ? "#067647" : Grey17,
                                                                fontWeight: choice.Picked ? 600 : 400}}>
                                                {choice.Picked ? "▸ " : "   "}{choice.Text}
                                                {choice.IsCorrect ? " ✓" : ""}
                                            </Typography>
                                            {choice.Rationale ? (
                                                <Typography style={{fontSize: 12, color: Grey6, marginLeft: 18}}>
                                                    {choice.Rationale}
                                                </Typography>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    ) : null}

                    {error ? (
                        <Typography style={{fontSize: 13, color: "#B42318"}}>{error}</Typography>
                    ) : null}
                </DialogContent>
                <DialogActions style={{padding: "12px 24px 20px"}}>
                    <Button onClick={() => { setTaking(null); setQuiz(null); setResult(null); }}
                            sx={{textTransform: "none"}}>Close</Button>
                    {result && !result.Passed ? (
                        <Button variant="outlined" disabled={busy}
                                onClick={() => { setResult(null); setPicked({}); }}
                                sx={{textTransform: "none"}}>Take it again</Button>
                    ) : null}
                    {result && result.Passed ? (
                        <Button variant="contained" disabled={busy}
                                onClick={() => taking && openSigning(taking)}
                                sx={{textTransform: "none"}}>Sign and record</Button>
                    ) : null}
                    {!result ? (
                        <Button variant="contained" disabled={busy} onClick={submit}
                                sx={{textTransform: "none"}}>Submit</Button>
                    ) : null}
                </DialogActions>
            </Dialog>

            {/* ── signing ────────────────────────────────────────────────────────────────── */}
            <Dialog open={!!signing} onClose={() => setSigning(null)} maxWidth="xs" fullWidth>
                <DialogTitle style={{fontSize: 20, fontWeight: 700, color: Grey17}}>Record your training</DialogTitle>
                <DialogContent>
                    <Typography style={{fontSize: 13, color: Grey5, marginBottom: 12}}>
                        {signing?.ObjectID} — {signing?.Name}
                    </Typography>
                    {signing?.RequiresAcknowledgment ? (
                        <FormControlLabel
                            control={<Checkbox size="small" checked={acknowledged}
                                               onChange={event => setAcknowledged(event.target.checked)}/>}
                            label={<Typography style={{fontSize: 14}}>
                                I confirm I have read and understood this record.
                            </Typography>}/>
                    ) : null}
                    {/* THE SIGNATURE. An electronic signature is made by the person whose name it carries,
                        not by whoever holds the session -- the same words the approval path uses. */}
                    <PasswordField fullWidth size="small" label="Your password" autoFocus
                               value={password} onChange={event => setPassword(event.target.value)}
                               onKeyDown={event => { if (event.key === "Enter" && password) sign(); }}
                               // 10px MORE WHEN THE CHECKBOX IS ABOVE IT. `marginTop: 2` is MUI
                               // spacing, i.e. 16px, and a FormControlLabel sits tight to what
                               // follows -- the confirmation and the signature read as one control
                               // rather than two separate things being asked of you. Ed: "the
                               // checkbox the field that collects my password are too close
                               // together." Unchanged when there is no checkbox, since then the
                               // spacing is against the record name and was never the complaint.
                               sx={{marginTop: signing?.RequiresAcknowledgment ? "26px" : 2}}
                               helperText="Recording training is signed for, so it asks for your password."/>
                    {error ? (
                        <Typography style={{fontSize: 13, color: "#B42318", marginTop: 12}}>{error}</Typography>
                    ) : null}
                </DialogContent>
                <DialogActions style={{padding: "12px 24px 20px"}}>
                    <Button onClick={() => setSigning(null)} sx={{textTransform: "none"}}>Cancel</Button>
                    <Button variant="contained" onClick={sign}
                            disabled={busy || !password || (signing?.RequiresAcknowledgment === true && !acknowledged)}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 3}}>
                        Sign
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
